/**
 * Волна D (docs/MIGRATION_ROUTER.md §5).
 * Было: <PrivateRoute path='/product/:id(\d+)' addMobileMargin>.
 * Regex-параметр эмулируется numericIdShell (не-число → 404);
 * addMobileMargin — через staticData (LayoutShell, волна E).
 */
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { numericIdShell } from '../compat/shells';
import ProductPage from '@routes/ProductPage/component';
import { getQueryClient } from '../../providers/query';
import { productInfoTempQueryOptions } from '@queries/index';
import { isContentNotFoundError } from '../../api/contentNotFound';
import { productUrl } from '@constants/site';

/**
 * ЭТАП 3 п.3 — 301 ИЗ КОЛОНКИ `juliet_catalog.redirect`.
 *
 * У товара в базе есть поле-преемник («Ссылка для перенаправления при
 * переходе на выключенный товар» — `Lazar/lib/models/catalog.ts`), и в проде
 * `lib/routes/product.ts` отдавал по нему 301. Это тот механизм, из-за
 * которого товарные страницы не теряли вес при ротации ассортимента: снятый
 * товар склеивался со своей заменой. У нас такой товар отдавал обычные 200.
 * Разбор — в `docs/SSR_LAZARSERVER_AUDIT.md`, п.4.
 *
 * Поле УЖЕ приезжает в `getProductInfoTemp` (оно там было и раньше, просто
 * никем не читалось), а сам товар мы и так дожидаемся ради честного 404 —
 * лишнего запроса нет. Важно, что бэк отдаёт и выключенный товар:
 * `getProductByIdTemp` ищет `findByPk` без фильтра по `active`, а
 * CONTENT_NOT_FOUND кидает только когда строки нет вовсе.
 */
const productRedirect = (id: string, data: unknown): void => {
  const target = (
    data as { getProductByIdTemp?: { redirect?: string | null } } | undefined
  )?.getProductByIdTemp?.redirect;

  const to = (target ?? '').trim();
  // Пустая цель и ссылка на саму себя — защита от пустого 301 и от петли
  if (!to || to === `/product/${id}`) {
    return;
  }
  throw redirect({ to, statusCode: 301 });
};

export const Route = createFileRoute('/product/$id')({
  staticData: { layout: { addMobileMargin: true } },
  /**
   * ЭТАП 2 — честный 404 несуществующего товара: notFound() рендерит контент
   * /404 (root.notFoundComponent = LegacyNotFound) в позиции Outlet и на SSR
   * отдаёт HTTP 404 (router-core.applyFailure) — БЕЗ редиректа, URL остаётся.
   *
   * ВАЖНО: лоадеры matched-роутов TanStack гонит ПАРАЛЛЕЛЬНО (root и товар
   * одновременно — читать кэш root-префетча отсюда нельзя, он ещё в полёте).
   * Поэтому ensureQueryData: react-query дедуплицирует по ключу с фетчем
   * префетча (тот же per-request QueryClient, тот же pristine-ключ) — лишнего
   * запроса нет, а результат здесь достоверен. Серверный клиент с retry:false
   * — падение мгновенное. Прочие ошибки (сеть и т.п.) 404 НЕ считаем —
   * страница рендерится как раньше, клиент дотягивает сам. На клиентской
   * навигации не блокируем (SPA-поведение прежнее).
   */
  loader: async ({ params }) => {
    // Не-число (/product/abc): раньше numericIdShell рендерил 404-контент
    // со статусом 200 — теперь и статус честный
    if (!/^\d+$/.test(params.id)) {
      throw notFound();
    }
    if (typeof window !== 'undefined') {
      return;
    }
    const queryClient = getQueryClient();
    const options = productInfoTempQueryOptions({
      id: Number(params.id),
      isMenu: false,
      xStoreId: null
    });
    // Префетч успел упасть раньше нас — ошибка уже в кэше, второй запрос
    // не нужен
    const settled = queryClient.getQueryState(options.queryKey);
    if (settled?.error) {
      if (isContentNotFoundError(settled.error)) {
        throw notFound();
      }
      return;
    }
    try {
      await queryClient.ensureQueryData(options);
    } catch (error) {
      if (isContentNotFoundError(error)) {
        throw notFound();
      }
      return;
    }
    productRedirect(params.id, queryClient.getQueryData(options.queryKey));
  },
  component: numericIdShell(ProductPage, '/product/:id(\\d+)', productUrl)
});
